import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useSupabaseAsesoriaPlans } from '../../../hooks/useSupabaseAsesoriaPlans';

export default function PlansSection() {
  const { data: plans, loading } = useSupabaseAsesoriaPlans();

  const { ref, style } = useScrollReveal({
    threshold: 0.1,
    distance: 32,
    duration: 800,
  });
  const { ref: refGrid, style: styleGrid } = useScrollReveal({
    threshold: 0.1,
    distance: 28,
    duration: 800,
    delay: 200,
  });

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.sort_order - b.sort_order),
    [plans]
  );

  return (
    <section
      id="planes"
      className="w-full bg-brand-cream py-20 md:py-28 px-6 lg:px-[7%]"
    >
      <div ref={ref} style={style} className="text-center max-w-2xl mx-auto">
        <span className="font-sans text-brand-gray-500 text-[11px] tracking-[0.2em] uppercase font-medium">
          Planes
        </span>
        <h2 className="font-serif text-brand-black text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.3] mt-4">
          Elige el nivel de transformación que necesitas
        </h2>
        <p className="font-sans text-brand-gray-600 text-base leading-relaxed mt-4">
          Todos los planes incluyen acceso directo al método de Anthony Sánchez
          y evaluación inicial sin compromiso.
        </p>
      </div>

      {loading && (
        <div className="mt-12 flex items-center justify-center gap-2 text-brand-gray-500 text-sm">
          <i className="ri-loader-4-line animate-spin" />
          <span>Cargando planes...</span>
        </div>
      )}

      <div
        ref={refGrid}
        style={styleGrid}
        className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12 max-w-5xl mx-auto items-start"
      >
        {sortedPlans.map((plan) => (
          <div
            key={plan.id}
            className={`relative rounded-2xl p-6 md:p-8 border ${
              plan.highlighted
                ? 'bg-brand-black border-brand-black'
                : 'bg-white border-brand-gray-200'
            }`}
          >
            {plan.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-block bg-brand-sage text-brand-black font-sans text-[11px] tracking-wider uppercase font-medium px-4 py-1.5 rounded-full whitespace-nowrap">
                  Más solicitado
                </span>
              </div>
            )}
            <h3
              className={`font-serif text-2xl font-medium ${
                plan.highlighted ? 'text-white' : 'text-brand-black'
              }`}
            >
              {plan.name}
            </h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span
                className={`font-serif text-3xl font-light ${
                  plan.highlighted ? 'text-white' : 'text-brand-black'
                }`}
              >
                {plan.price}
              </span>
              <span
                className={`font-sans text-sm ${
                  plan.highlighted ? 'text-white/60' : 'text-brand-gray-500'
                }`}
              >
                {plan.period}
              </span>
            </div>
            <p
              className={`font-sans text-sm leading-relaxed mt-4 ${
                plan.highlighted ? 'text-white/70' : 'text-brand-gray-600'
              }`}
            >
              {plan.description}
            </p>
            <ul className="mt-6 space-y-3">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-brand-sage" />
                  </div>
                  <span
                    className={`font-sans text-sm leading-relaxed ${
                      plan.highlighted
                        ? 'text-white/80'
                        : 'text-brand-gray-600'
                    }`}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                to="/contacto"
                className={`block text-center font-sans text-sm font-medium tracking-wide px-6 py-3.5 rounded-full transition-colors duration-300 whitespace-nowrap ${
                  plan.highlighted
                    ? 'bg-white text-brand-black hover:bg-brand-sage'
                    : 'bg-brand-black text-white hover:bg-brand-gray-600'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}